import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Mail, MessageCircle, Phone, User, Check, AlertCircle, Crown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { usePersonProfiles, PersonProfile } from '@/hooks/usePersonProfiles';
import { useFamilySubscription } from '@/hooks/useFamilySubscription';
import { ModalLoadingSkeleton } from '@/components/ui/modal-loading-skeleton';
import { ModalFooter } from '@/components/ui/modal-footer';
import { generateInvitationUrl } from '@/lib/urlUtils';
import { useNavigate } from 'react-router-dom';

interface InvitationModalProps {
  familyId: string;
  linkedPersonId?: string; // ID de la personne à lier automatiquement
  onClose: () => void;
  onSuccess?: () => void;
}

type InvitationChannel = 'email' | 'whatsapp' | 'sms';

interface InvitationFormData {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  channel: InvitationChannel;
  message: string;
  role: 'spouse' | 'parent' | 'child' | 'guardian';
}

export function InvitationModal({ familyId, linkedPersonId, onClose, onSuccess }: InvitationModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { persons } = usePersonProfiles(familyId);
  const { canInvite, loading: subscriptionLoading } = useFamilySubscription(familyId);
  
  const [formData, setFormData] = useState<InvitationFormData>({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    channel: 'email',
    message: '',
    role: 'spouse'
  });
  
  const [selectedPerson, setSelectedPerson] = useState<PersonProfile | null>(
    linkedPersonId ? persons.find(p => p.id === linkedPersonId) || null : null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const channelIcons = {
    email: Mail,
    whatsapp: MessageCircle,
    sms: Phone
  };

  const channelLabels = {
    email: 'Email',
    whatsapp: 'WhatsApp',
    sms: 'SMS'
  };

  // Simulate minimal loading delay to prevent flash
  useEffect(() => {
    const timer = setTimeout(() => setInitializing(false), 200);
    return () => clearTimeout(timer);
  }, []);

  const mapFamilyRoleToInvitationRole = (familyRole: string | null): 'spouse' | 'parent' | 'child' | 'guardian' => {
    if (!familyRole) return 'spouse';
    
    // Map database family roles to invitation roles
    switch (familyRole) {
      case 'spouse':
        return 'spouse';
      case 'parent':
      case 'father':
      case 'mother':
        return 'parent';
      case 'child':
      case 'son':
      case 'daughter':
        return 'child';
      case 'guardian':
      case 'guardian_male':
      case 'guardian_female':
      case 'grandparent':
      case 'grandfather':
      case 'grandmother':
        return 'guardian';
      default:
        return 'spouse';
    }
  };

  // Initialize with linked person data if provided
  useEffect(() => {
    if (linkedPersonId && persons.length > 0) {
      const person = persons.find(p => p.id === linkedPersonId);
      if (person) {
        setSelectedPerson(person);
        setFormData(prev => ({
          ...prev,
          firstName: person.first_name,
          lastName: person.last_name,
          email: person.email || '',
          phone: person.phone_primary || '',
          // Pre-select role based on person's family_role from database
          role: mapFamilyRoleToInvitationRole(person.family_role)
        }));
      }
    }
  }, [linkedPersonId, persons]);

  const getCategoryInFrench = (category: string) => {
    const categoryTranslations: { [key: string]: string } = {
      'friends': 'Amis',
      'family': 'Famille',
      'extended_family': 'Famille étendue',
      'medical_other': 'Médical/Autres',
      'nannies_helpers': 'Nounous, aide',
      'colleagues': 'Collègues',
      'neighbours': 'Voisins',
      'professionals': 'Professionnels',
      'other': 'Autre'
    };
    return categoryTranslations[category] || category;
  };

  const getDefaultMessage = () => {
    const channelText = {
      email: "🔗 Créez votre compte en cliquant sur le lien ci-dessous",
      whatsapp: "📱 Téléchargez Clan et créez votre compte avec le lien",  
      sms: "📱 Téléchargez Clan sur votre téléphone avec ce lien"
    };

    const personName = formData.firstName || selectedPerson?.first_name || '';
    const greeting = personName ? `Bonjour ${personName} !` : 'Bonjour !';

    return `${greeting} 🎉

Je vous invite à rejoindre notre famille sur Clan pour que nous puissions partager nos calendriers, tâches et informations importantes en toute simplicité.

${channelText[formData.channel]}

À très bientôt sur Clan ! ✨

PS: Clan est l'app familiale qui simplifie l'organisation du quotidien.`;
  };

  const handlePersonSelect = (personId: string) => {
    const person = persons.find(p => p.id === personId);
    if (person) {
      setSelectedPerson(person);
      setFormData(prev => ({
        ...prev,
        firstName: person.first_name,
        lastName: person.last_name,
        email: person.email || '',
        phone: person.phone_primary || '',
        // Pre-select role based on person's family_role from database
        role: mapFamilyRoleToInvitationRole(person.family_role)
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.email && formData.channel === 'email') {
      toast({
        title: "Erreur",
        description: "L'email est requis pour ce canal d'invitation",
        variant: "destructive",
      });
      return;
    }

    if (!formData.phone && (formData.channel === 'whatsapp' || formData.channel === 'sms')) {
      toast({
        title: "Erreur",
        description: "Le numéro de téléphone est requis pour ce canal d'invitation",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Get current user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (profileError) throw profileError;

      // Get family info
      const { data: family, error: familyError } = await supabase
        .from('families')
        .select('name')
        .eq('id', familyId)
        .single();

      if (familyError) throw familyError;

      // Create or refresh invitation with upsert-like behavior
      const nowPlus7 = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const newToken = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      let invitation: any;
      // Check if there's already a family member with this email
      // First, get the user_id from profiles with this email
      const { data: profileWithEmail, error: emailProfileError } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('email', formData.email.toLowerCase())
        .maybeSingle();

      if (emailProfileError) {
        console.error('Error checking profile:', emailProfileError);
        toast({
          title: "Erreur",
          description: "Erreur lors de la vérification du profil",
          variant: "destructive",
        });
        return;
      }

      // If a profile exists with this email, check if they're already a family member
      if (profileWithEmail) {
        const { data: existingMember, error: checkMemberError } = await supabase
          .from('family_memberships')
          .select('id')
          .eq('family_id', familyId)
          .eq('user_id', profileWithEmail.user_id)
          .maybeSingle();

        if (checkMemberError) {
          console.error('Error checking existing member:', checkMemberError);
          toast({
            title: "Erreur",
            description: "Erreur lors de la vérification des membres existants",
            variant: "destructive",
          });
          return;
        }

        if (existingMember) {
          toast({
            title: "Erreur",
            description: "Cette personne fait déjà partie du clan",
            variant: "destructive",
          });
          return;
        }
      }

      const insertResult = await supabase
        .from('family_invitations')
        .insert({
          family_id: familyId,
          invited_by: profile.id,
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
          role: formData.role,
          message: formData.message.trim() || getDefaultMessage(),
          status: 'pending' as const,
          linked_person_id: selectedPerson?.id || null,
          invitation_channel: formData.channel,
          token: newToken,
          expires_at: nowPlus7,
          accepted_at: null
        })
        .select()
        .single();

      if (insertResult.error && insertResult.error.code === '23505') {
        // Invitation already exists for this family + email -> refresh it
        const { data: updated, error: updateError } = await supabase
          .from('family_invitations')
          .update({
            phone: formData.phone.trim() || null,
            role: formData.role,
            message: formData.message.trim() || getDefaultMessage(),
            status: 'pending' as const,
            linked_person_id: selectedPerson?.id || null,
            invitation_channel: formData.channel,
            token: newToken,
            expires_at: nowPlus7,
            accepted_at: null
          })
          .eq('family_id', familyId)
          .eq('email', formData.email.trim())
          .select()
          .single();
        if (updateError) throw updateError;
        invitation = updated;
      } else if (insertResult.error) {
        throw insertResult.error;
      } else {
        invitation = insertResult.data;
      }

      // Send invitation based on selected channel
      let sendResult;
      const invitationData = {
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        token: invitation.token,
        familyName: family.name,
        inviterName: `${profile.first_name} ${profile.last_name}`,
        message: formData.message.trim() || getDefaultMessage(),
        linkedPersonName: selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}` : null
      };

      switch (formData.channel) {
        case 'email':
          sendResult = await supabase.functions.invoke('send-invitation-email', {
            body: invitationData
          });
          break;
        case 'whatsapp':
          // Enhanced WhatsApp message with emojis and better formatting
          const customMsg = formData.message.trim() || getDefaultMessage();
          const whatsappMessage = `🎉 ${invitationData.inviterName} vous invite dans la famille "${invitationData.familyName}" sur Clan !

📱 ${customMsg.replace(/\n/g, '\n\n')}

🔗 Rejoignez-nous ici : ${generateInvitationUrl(invitation.token)}

✨ Clan - L'app qui simplifie l'organisation familiale`;
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
          window.location.href = whatsappUrl;
          sendResult = { data: { success: true } };
          break;
        case 'sms':
          // Enhanced SMS message
          const smsCustomMsg = formData.message.trim() || getDefaultMessage();
          const smsMessage = `${invitationData.inviterName} vous invite dans la famille "${invitationData.familyName}" sur Clan. ${smsCustomMsg.slice(0, 100)}... Lien: ${generateInvitationUrl(invitation.token)}`;
          const smsUrl = `sms:?body=${encodeURIComponent(smsMessage)}`;
          window.location.href = smsUrl;
          sendResult = { data: { success: true } };
          break;
      }

      if (sendResult?.error) {
        console.error(`Error sending ${formData.channel} invitation:`, sendResult.error);
        
        // Fallback: copy link to clipboard using invitation metadata URL
        const invitationUrl = generateInvitationUrl(invitation.token);
        try {
          await navigator.clipboard.writeText(invitationUrl);
          toast({
            title: "Lien copié dans le presse-papier",
            description: `Impossible d'envoyer via ${channelLabels[formData.channel]}. Vous pouvez maintenant partager le lien manuellement.`,
            variant: "default",
          });
        } catch {
          toast({
            title: "Erreur d'envoi",
            description: `Voici le lien à partager manuellement : ${invitationUrl}`,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Invitation envoyée",
          description: `L'invitation a été envoyée via ${channelLabels[formData.channel]} ${selectedPerson ? `et sera automatiquement liée au profil de ${selectedPerson.first_name}` : ''}.`,
        });
      }

      onSuccess?.();
      onClose();
      
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (initializing || subscriptionLoading) {
    return (
      <>
        <div className="flex-1 overflow-y-auto">
          <ModalLoadingSkeleton type="person" />
        </div>
      </>
    );
  }

  // Show upgrade prompt as overlay instead of replacing entire form

  const isFormValid = formData.firstName.trim() && formData.lastName.trim() && 
    ((formData.channel === 'email' && formData.email.trim()) ||
     ((formData.channel === 'whatsapp' || formData.channel === 'sms') && formData.phone.trim()));

  return (
    <>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6 max-w-md mx-auto">
          {/* Person Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Lier à une personne existante (optionnel)</Label>
            <p className="text-sm text-muted-foreground">
              Si cette invitation concerne une personne déjà dans votre carnet, sélectionnez-la pour lier automatiquement son profil.
            </p>
            
            {selectedPerson ? (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={selectedPerson.avatar_url} />
                  <AvatarFallback>
                    {selectedPerson.first_name.charAt(0)}{selectedPerson.last_name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{selectedPerson.first_name} {selectedPerson.last_name}</p>
                  <p className="text-sm text-muted-foreground">{getCategoryInFrench(selectedPerson.category)}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedPerson(null)}
                >
                  Retirer
                </Button>
              </div>
            ) : (
              <Select onValueChange={handlePersonSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une personne..." />
                </SelectTrigger>
                <SelectContent>
                  {persons.map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {person.first_name} {person.last_name}
                        <Badge variant="outline" className="text-xs">
                          {getCategoryInFrench(person.category)}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">Prénom *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Nom *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </div>
          </div>

          {/* Channel Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Canal d'invitation</Label>
            <div className="grid grid-cols-3 gap-3">
              {(['email', 'whatsapp', 'sms'] as InvitationChannel[]).map((channel) => {
                const Icon = channelIcons[channel];
                return (
                  <button
                    key={channel}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, channel }))}
                    className={`p-4 border-2 rounded-lg transition-colors ${
                      formData.channel === channel
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon className="h-6 w-6 mx-auto mb-2" />
                    <div className="text-sm font-medium">{channelLabels[channel]}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contact Fields */}
          {formData.channel === 'email' && (
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
          )}

          {(formData.channel === 'whatsapp' || formData.channel === 'sms') && (
            <div className="space-y-2">
              <Label htmlFor="phone">Numéro de téléphone *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+33 6 12 34 56 78"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </div>
          )}

          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="role">Rôle dans la famille</Label>
            <Select value={formData.role} onValueChange={(value: any) => setFormData(prev => ({ ...prev, role: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="spouse">Conjoint(e)</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="child">Enfant</SelectItem>
                <SelectItem value="guardian">Tuteur/Tutrice</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Message Preview */}
          <div className="space-y-2">
            <Label htmlFor="message">Message personnalisé</Label>
            <Textarea
              id="message"
              value={formData.message || getDefaultMessage()}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              rows={6}
              className="resize-none"
            />
          </div>

          {/* Preview Summary */}
          <div className="p-4 border rounded-lg bg-muted/20">
            <h4 className="font-semibold mb-3">Aperçu de l'invitation</h4>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Destinataire:</span>
                <span className="font-medium">{formData.firstName} {formData.lastName}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Canal:</span>
                <div className="flex items-center gap-2">
                  {(() => {
                    const Icon = channelIcons[formData.channel];
                    return <Icon className="h-4 w-4" />;
                  })()}
                  <span className="font-medium">{channelLabels[formData.channel]}</span>
                </div>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Contact:</span>
                <span className="font-medium">
                  {formData.channel === 'email' ? formData.email : formData.phone}
                </span>
              </div>

              {selectedPerson && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Lié à:</span>
                  <span className="font-medium">{selectedPerson.first_name} {selectedPerson.last_name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!canInvite && (
        <div className="mx-6 mb-4 p-4 bg-primary/5 border-2 border-primary/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Crown className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-2">
                Passez à Clan Pro pour inviter des membres
              </p>
              <Button
                onClick={() => {
                  onClose();
                  navigate('/upgrade');
                }}
                size="sm"
                className="w-full"
              >
                Découvrir Clan Pro
              </Button>
            </div>
          </div>
        </div>
      )}

      <ModalFooter
        primaryLabel="Envoyer l'invitation"
        onPrimary={handleSubmit}
        onCancel={onClose}
        variant="default"
        loading={isLoading}
        disabled={!isFormValid}
      />
    </>
  );
}